<template>
  <vue-final-modal
    v-model="isOpen"
    classes="modal-overlay-container"
    content-class="modern-modal-content confirm-modal-content"
    @before-close="closeDialog"
  >
    <div class="modal-header">
      <div class="header-text-container">
        <div class="title-wrapper">
          <div class="title-icon" :class="iconVariantClass">
            <!-- Reset / Undo Icon -->
            <svg v-if="type === 'reset' || type === 'undo'" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
              <path d="M3 3v5h5"></path>
            </svg>
            <!-- Warning / Alert Icon -->
            <svg v-else-if="type === 'warning'" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            <!-- Danger / Trash Icon -->
            <svg v-else-if="type === 'danger'" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
            <!-- Info / Question Default Icon -->
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
          </div>
          <h5 class="modal-title">{{ title || 'Confirmation' }}</h5>
        </div>
      </div>
      <button type="button" class="close-button" aria-label="Close" @click="closeDialog">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

    <div class="modal-body text-center py-4">
      <p class="confirm-message">{{ message || 'Are you sure you want to proceed?' }}</p>
      <slot name="extra"></slot>
    </div>

    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" @click="closeDialog">
        {{ cancelButtonText || 'Cancel' }}
      </button>
      <button
        type="button"
        class="btn"
        :class="confirmBtnClass"
        @click="handleConfirm"
      >
        <span v-if="loading" class="spinner-border spinner-border-sm me-1" role="status"></span>
        {{ confirmButtonText || 'Confirm' }}
      </button>
    </div>
  </vue-final-modal>
</template>

<script>
export default {
  name: 'ConfirmModal',
  props: {
    modelValue: {
      type: Boolean,
      default: undefined
    },
    dialog: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Confirmation'
    },
    message: {
      type: String,
      default: 'Are you sure you want to proceed?'
    },
    type: {
      type: String,
      default: 'warning' // 'warning' | 'danger' | 'reset' | 'undo' | 'info'
    },
    confirmButtonText: {
      type: String,
      default: 'Confirm'
    },
    cancelButtonText: {
      type: String,
      default: 'Cancel'
    },
    confirmVariant: {
      type: String,
      default: '' // 'primary' | 'warning' | 'danger' | 'secondary'
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'confirm', 'onChange', 'close'],
  computed: {
    isOpen: {
      get() {
        return this.modelValue !== undefined ? this.modelValue : this.dialog;
      },
      set(val) {
        if (this.modelValue !== undefined) {
          this.$emit('update:modelValue', val);
        } else {
          if (!val) {
            this.$emit('close');
          }
        }
      }
    },
    iconVariantClass() {
      if (this.type === 'danger') return 'icon-danger';
      if (this.type === 'warning') return 'icon-warning';
      if (this.type === 'reset' || this.type === 'undo') return 'icon-theme';
      return 'icon-info';
    },
    confirmBtnClass() {
      if (this.confirmVariant) {
        return `btn-${this.confirmVariant}`;
      }
      if (this.type === 'danger') return 'btn-danger';
      if (this.type === 'warning') return 'btn-warning';
      if (this.type === 'reset' || this.type === 'undo') return 'btn-primary';
      return 'btn-primary';
    }
  },
  methods: {
    handleConfirm() {
      this.$emit('confirm');
      this.$emit('onChange', { confirmed: true });
      this.isOpen = false;
    },
    closeDialog() {
      this.$emit('close');
      this.isOpen = false;
    }
  }
};
</script>

<style scoped>
:deep(.modal-overlay-container) {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1050;
}

:deep(.confirm-modal-content) {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  border: none !important;
  padding: 0;
  margin: 0 1rem;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  background-color: #ffffff;
}

.title-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.title-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 8px;
}

.title-icon.icon-danger {
  color: #dc3545;
  background: rgba(220, 53, 69, 0.1);
}

.title-icon.icon-warning {
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.1);
}

.title-icon.icon-theme {
  color: #00a0c1;
  background: rgba(0, 160, 193, 0.1);
}

.title-icon.icon-info {
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
}

.modal-title {
  color: #1e293b;
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
}

.close-button {
  background: #f1f5f9;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #94a3b8;
  transition: all 0.2s;
}

.close-button:hover {
  background-color: #fee2e2;
  color: #dc3545;
  transform: rotate(90deg);
}

.modal-body {
  padding: 1.5rem;
  flex-grow: 1;
}

.confirm-message {
  font-size: 1rem;
  color: #334155;
  margin: 0;
  font-weight: 500;
  line-height: 1.5;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #f1f5f9;
  padding: 1rem 1.5rem;
  gap: 0.75rem;
  background-color: #f8fafc;
}

.btn {
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
}

.btn-secondary {
  background-color: white;
  color: #64748b;
  border-color: #e2e8f0;
}

.btn-secondary:hover {
  background-color: #f8fafc;
  border-color: #cbd5e1;
  color: #1e293b;
}

.btn-primary {
  background-color: #00a0c1;
  border-color: #00a0c1;
  color: white;
}

.btn-primary:hover {
  background-color: #008ba8;
}

.btn-warning {
  background-color: #f59e0b;
  border-color: #f59e0b;
  color: white;
}

.btn-warning:hover {
  background-color: #d97706;
}

.btn-danger {
  background-color: #ef4444;
  color: white;
}

.btn-danger:hover {
  background-color: #dc2626;
  box-shadow: 0 4px 6px -1px rgba(220, 38, 38, 0.2);
}
</style>
